// Drawer business details. No orders, invoices, customer records or discounts are created here.
// Keep draft edits in memory across Horizon's section morphs; never store personal data in localStorage.
const state = { initialized: false, selected: false, name: '', org: '', revision: 0, saved: 0 };
let timer;
let saving;
let submitting = false;
const allowedForms = new WeakSet();
const fields = () => document.querySelectorAll('rh-cart-business');
const root = () => window.Shopify?.routes?.root || '/';
const normalizedOrg = (value) => value.replace(/\s/g, '');

function paint() {
  fields().forEach((widget) => widget.paint());
}

function status(message = '', error = false) {
  fields().forEach((widget) => {
    const node = widget.querySelector('[data-business-status]');
    node.textContent = message;
    node.hidden = !message;
    widget.toggleAttribute('data-save-error', error);
  });
}

async function save() {
  clearTimeout(timer);
  if (saving) {
    await saving;
    if (state.saved < state.revision) return save();
    return;
  }
  if (state.saved >= state.revision) return;
  const revision = state.revision;
  const attributes = {
    'Handler som bedrift': state.selected ? 'Ja' : '',
    'Bedriftsnavn': state.selected ? state.name.trim() : '',
    'Organisasjonsnummer': state.selected ? normalizedOrg(state.org) : '',
  };
  status('Lagrer …');
  saving = (async () => {
    const response = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
      signal: AbortSignal.timeout(12000),
    });
    if (!response.ok) throw new Error('Could not save business details');
    const cart = await response.json();
    for (const [key, value] of Object.entries(attributes)) {
      if ((cart.attributes?.[key] || '') !== value) throw new Error('Business details were not confirmed');
    }
    state.saved = revision;
    status();
  })();
  try {
    await saving;
  } catch (error) {
    status('Kunne ikke lagre. Prøv igjen ved å trykke «Til kassen».', true);
    throw error;
  } finally {
    saving = null;
  }
  if (state.saved < state.revision) return save();
}

function changed() {
  state.revision += 1;
  clearTimeout(timer);
  timer = setTimeout(() => { save().catch(() => {}); }, 450);
}

class CartBusiness extends HTMLElement {
  connectedCallback() {
    if (!this.querySelector('[data-business-toggle]')) return;
    if (!state.initialized) {
      state.selected = this.querySelector('[data-business-toggle]').checked;
      state.name = this.querySelector('[data-business-name]').value;
      state.org = this.querySelector('[data-business-org]').value;
      state.initialized = true;
    }
    this.abort?.abort();
    this.abort = new AbortController();
    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-business-toggle]')) return;
      state.selected = event.target.checked;
      if (!state.selected) { state.name = ''; state.org = ''; }
      this.paint();
      changed();
      // Opening the fields changes the sticky summary's height.
      const dialog = this.closest('dialog');
      const summary = this.closest('.cart-drawer__summary');
      if (dialog && summary) {
        dialog.setAttribute('cart-summary-sticky', String(summary.offsetHeight / dialog.offsetHeight <= 0.5));
      }
    }, { signal: this.abort.signal });
    this.addEventListener('input', (event) => {
      if (event.target.matches('[data-business-name]')) state.name = event.target.value;
      else if (event.target.matches('[data-business-org]')) state.org = event.target.value;
      else return;
      event.target.setCustomValidity('');
      changed();
    }, { signal: this.abort.signal });
    this.paint();
  }

  disconnectedCallback() { this.abort?.abort(); }

  paint() {
    const toggle = this.querySelector('[data-business-toggle]');
    const panel = this.querySelector('[data-business-fields]');
    const name = this.querySelector('[data-business-name]');
    const org = this.querySelector('[data-business-org]');
    if (!toggle || !panel || !name || !org) return;
    toggle.checked = state.selected;
    toggle.setAttribute('aria-expanded', String(state.selected));
    panel.hidden = !state.selected;
    this.toggleAttribute('data-selected', state.selected);
    for (const [input, value] of [[name, state.name], [org, state.org]]) {
      input.disabled = !state.selected;
      input.required = state.selected;
      if (input.value !== value) input.value = value;
      if (!state.selected) input.setCustomValidity('');
    }
  }

  validate() {
    if (!state.selected) return true;
    const name = this.querySelector('[data-business-name]');
    const org = this.querySelector('[data-business-org]');
    if (!(name instanceof HTMLInputElement) || !(org instanceof HTMLInputElement)) return false;
    // Native form submission must carry the same normalized values as the Ajax cart save.
    const finalName = name.value.trim();
    const finalOrg = normalizedOrg(org.value);
    if (state.name !== finalName || state.org !== finalOrg) {
      state.name = finalName;
      state.org = finalOrg;
      state.revision += 1;
    }
    name.value = finalName;
    org.value = finalOrg;
    name.setCustomValidity(state.name ? '' : 'Skriv inn bedriftsnavnet.');
    org.setCustomValidity(/^\d{9}$/.test(normalizedOrg(state.org)) ? '' : 'Skriv inn et norsk organisasjonsnummer med 9 siffer.');
    return name.reportValidity() && org.reportValidity();
  }
}

if (!customElements.get('rh-cart-business')) customElements.define('rh-cart-business', CartBusiness);

// Repaint only after the section update completes; listeners survive replacement of the drawer HTML.
document.addEventListener('cart:update', () => requestAnimationFrame(paint));
document.addEventListener('discount:update', () => requestAnimationFrame(paint));

async function checkout(form, button, widget) {
  if (submitting || !widget.validate()) return;
  submitting = true;
  const wasDisabled = button?.disabled;
  if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
  try {
    // Persist the final values even when checkout is clicked before the autosave timer fires.
    if (state.selected) state.revision += 1;
    await save();
    paint();
    if (button) button.disabled = Boolean(wasDisabled);
    if (!form.reportValidity()) return;
    allowedForms.add(form);
    form.requestSubmit(button || undefined);
  } catch {
    status('Opplysningene ble ikke lagret. Prøv «Til kassen» igjen, eller fjern bedriftsvalget.', true);
  } finally {
    submitting = false;
    if (button) { button.disabled = Boolean(wasDisabled); button.removeAttribute('aria-busy'); }
  }
}

// Capture clicks before native validation (whitespace-only names and spaced org.nr. need normalization).
document.addEventListener('click', (event) => {
  const button = event.target.closest?.('.cart__checkout-button');
  const widget = button?.closest('.cart__ctas')?.querySelector('rh-cart-business');
  if (!widget || (!state.selected && state.saved >= state.revision)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (button.form) checkout(button.form, button, widget);
}, true);

// Keyboard / Enter submission follows the same save-before-checkout path.
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (allowedForms.has(form)) { allowedForms.delete(form); return; }
  const widget = [...fields()].find((item) => item.querySelector('[data-business-name]')?.form === form);
  if (!widget || (!state.selected && state.saved >= state.revision)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const button = event.submitter || widget.closest('.cart__ctas')?.querySelector('.cart__checkout-button');
  checkout(form, button, widget);
}, true);
